import { useEffect, useRef, useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  NativeSelect,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@crewfsm/ui/app";
import {
  CalendarClock,
  ArrowRightLeft,
  Archive,
  MoreHorizontal,
  Pause,
  Play,
  UserRound,
} from "lucide-react";
import type {
  PendingJobPipelineReasonCode,
  WorkflowActionContext,
  WorkflowActionRequest,
  WorkflowPipelineItem,
  WorkflowQuickAction,
} from "@crewfsm/contracts";
import { PipelineApiError, type PipelineApi } from "./pipeline-api";
import { followUpInput, followUpInstant } from "./workflow-time";

import {
  LEAD_ACTION_LABELS,
  leadActionsFor,
  type LeadAction,
} from "./lead-actions";

export type PipelineAction = WorkflowQuickAction | LeadAction;
const LABELS: Record<PipelineAction, string> = {
  ...LEAD_ACTION_LABELS,
  FOLLOW_UP: "Follow-up",
  ASSIGN_OWNER: "Office owner",
  HOLD: "Put on hold",
  RESUME: "Resume work",
};
const ICONS = {
  LEAD_STATUS: ArrowRightLeft,
  LEAD_ASSIGN: UserRound,
  LEAD_ARCHIVE: Archive,
  FOLLOW_UP: CalendarClock,
  ASSIGN_OWNER: UserRound,
  HOLD: Pause,
  RESUME: Play,
};
const REASONS: Record<PendingJobPipelineReasonCode, string> = {
  CUSTOMER_RESPONSE_PENDING: "Waiting for customer",
  ESTIMATE_REQUIRED: "Estimate required",
  APPROVED_AWAITING_SCHEDULE: "Awaiting schedule",
  CUSTOMER_REQUEST: "Customer request",
  WAITING_FOR_PARTS: "Waiting for parts",
  CAPACITY_CONSTRAINT: "Team capacity",
  INTERNAL_REVIEW: "Internal review",
  SCOPE_CHANGED: "Scope changed",
  OTHER: "Other",
};
export type OpenWorkflowAction = (
  item: WorkflowPipelineItem,
  action: PipelineAction,
  trigger: HTMLButtonElement | null,
) => void;
export function WorkflowActionMenu({
  item,
  onAction,
  permissions = [],
}: {
  item: WorkflowPipelineItem;
  onAction?: OpenWorkflowAction;
  permissions?: readonly string[];
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const opening = useRef(false);
  const leadActions = leadActionsFor(item, permissions);
  const actions: PipelineAction[] = [
    ...leadActions.filter((action) => action !== "LEAD_ARCHIVE"),
    ...item.quickActions,
    ...leadActions.filter((action) => action === "LEAD_ARCHIVE"),
  ];
  if (!onAction || !actions.length) return null;
  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        if (open) opening.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          ref={trigger}
          variant="ghost"
          size="icon-sm"
          className="workflow-action-trigger size-10 shrink-0 md:size-8"
          aria-label={`Actions for ${item.title}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="workflow-theme min-w-44"
        onCloseAutoFocus={(event) => {
          if (opening.current) event.preventDefault();
        }}
      >
        {actions.map((action) => {
          const Icon = ICONS[action];
          return (
            <DropdownMenuItem
              key={action}
              variant={action === "LEAD_ARCHIVE" ? "destructive" : "default"}
              className="min-h-9"
              onSelect={() => {
                opening.current = true;
                onAction(item, action, trigger.current);
              }}
            >
              <Icon />
              {LABELS[action]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function WorkflowActionPanel({
  item,
  action,
  locationId,
  api,
  onClose,
  onSaved,
  onReturnFocus,
}: {
  item: WorkflowPipelineItem;
  action: WorkflowQuickAction;
  locationId: string;
  api: PipelineApi;
  onClose: () => void;
  onSaved: (message: string) => void;
  onReturnFocus: () => void;
}) {
  const [context, setContext] = useState<WorkflowActionContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [reload, setReload] = useState(0);
  const [time, setTime] = useState("");
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState<PendingJobPipelineReasonCode | "">("");
  const mounted = useRef(true);
  const saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    setStale(false);
    setContext(null);
    void api
      .actionContext(item.resource, locationId)
      .then((result) => {
        if (!current) return;
        setContext(result);
        setTime(
          result.followUpAt
            ? followUpInput(result.followUpAt, result.timezone)
            : "",
        );
        setOwner(result.ownerMembershipId ?? "");
        setReason(result.holdReasonCode ?? "");
      })
      .catch((cause: unknown) => {
        if (current) setError(actionError(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [api, item.resource, locationId, reload]);
  const allowed = context?.availableActions.includes(action);
  const save = async (clearReminder = false) => {
    if (!context || !allowed || saving.current || stale) return;
    let request: WorkflowActionRequest;
    const expectedUpdatedAt = context.expectedUpdatedAt;
    try {
      if (action === "FOLLOW_UP") {
        const followUpAt = clearReminder
          ? null
          : followUpInstant(time, context.timezone);
        if (followUpAt && Date.parse(followUpAt) <= Date.now())
          throw new Error("Choose a future date and time.");
        request = { action, expectedUpdatedAt, followUpAt };
      } else if (action === "ASSIGN_OWNER")
        request = {
          action,
          expectedUpdatedAt,
          ownerMembershipId: owner || null,
        };
      else if (action === "HOLD") {
        if (!reason)
          throw new Error("Choose a reason for putting this work on hold.");
        request = { action, expectedUpdatedAt, reasonCode: reason };
      } else request = { action, expectedUpdatedAt };
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Check the details and try again.",
      );
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.act(item.resource, locationId, request);
      if (mounted.current)
        onSaved(
          action === "FOLLOW_UP"
            ? clearReminder
              ? "Follow-up removed."
              : "Follow-up saved."
            : action === "ASSIGN_OWNER"
              ? "Office owner updated."
              : action === "HOLD"
                ? "Work put on hold."
                : "Work resumed.",
        );
    } catch (cause) {
      if (mounted.current) {
        setError(actionError(cause));
        setStale(
          cause instanceof PipelineApiError &&
            [403, 404, 409].includes(cause.status),
        );
      }
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open && !saving.current) onClose();
      }}
    >
      <SheetContent
        className="workflow-theme workflow-action-panel gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-none data-[side=right]:md:max-w-sm"
        showCloseButton={!busy}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onReturnFocus();
        }}
        onInteractOutside={(event) => {
          if (saving.current) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (saving.current) event.preventDefault();
        }}
      >
        <SheetHeader className="border-b p-5 pr-14">
          <SheetTitle>{LABELS[action]}</SheetTitle>
          <SheetDescription className="mt-2 [overflow-wrap:anywhere]">
            {item.title} · {item.customerName}
          </SheetDescription>
        </SheetHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {loading ? (
              <div
                role="status"
                aria-label="Loading action details"
                className="space-y-3"
              >
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : null}
            {error ? (
              <div role="alert" className="mb-5 space-y-3 text-sm">
                <p>{error}</p>
                {stale || !context ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReload((value) => value + 1)}
                  >
                    {stale ? "Reload details" : "Try again"}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {!loading && context && !allowed ? (
              <p role="status" className="text-sm text-muted-foreground">
                This work has moved on. Close this panel and refresh the board.
              </p>
            ) : null}
            {context && allowed ? (
              <fieldset
                disabled={busy || stale}
                className="min-w-0 space-y-5 disabled:opacity-60"
              >
                {action === "FOLLOW_UP" ? (
                  <div className="space-y-2">
                    <Label htmlFor="workflow-follow-up">Follow-up time</Label>
                    <Input
                      id="workflow-follow-up"
                      autoFocus
                      type="datetime-local"
                      required
                      className="h-10"
                      value={time}
                      onChange={(event) => {
                        setTime(event.target.value);
                        setError(null);
                      }}
                      aria-describedby="workflow-follow-up-hint"
                    />
                    <p
                      id="workflow-follow-up-hint"
                      className="text-xs text-muted-foreground"
                    >
                      Time in {context.timezone}.
                    </p>
                    <p className="pt-2 text-sm text-muted-foreground">
                      Remind{" "}
                      {context.owners.find(
                        (entry) =>
                          entry.membershipId === context.ownerMembershipId,
                      )?.displayName ?? "the office team"}{" "}
                      to take the next step.
                    </p>
                    {context.followUpAt ? (
                      <Button
                        variant="link"
                        className="mt-2 h-9"
                        onClick={() => void save(true)}
                      >
                        Remove follow-up
                      </Button>
                    ) : null}
                  </div>
                ) : null}
                {action === "ASSIGN_OWNER" ? (
                  <div className="space-y-2">
                    <Label htmlFor="workflow-office-owner">Office owner</Label>
                    <NativeSelect
                      id="workflow-office-owner"
                      autoFocus
                      className="w-full [&_select]:h-10"
                      value={owner}
                      onChange={(event) => setOwner(event.target.value)}
                      aria-describedby="workflow-owner-hint"
                    >
                      <option value="">Unassigned</option>
                      {context.ownerMembershipId &&
                      !context.owners.some(
                        (entry) =>
                          entry.membershipId === context.ownerMembershipId,
                      ) ? (
                        <option value={context.ownerMembershipId} disabled>
                          Previous owner unavailable
                        </option>
                      ) : null}
                      {context.owners.map((entry) => (
                        <option
                          key={entry.membershipId}
                          value={entry.membershipId}
                        >
                          {entry.displayName ?? entry.email ?? "Office member"}
                        </option>
                      ))}
                    </NativeSelect>
                    <p
                      id="workflow-owner-hint"
                      className="text-sm text-muted-foreground"
                    >
                      Responsible for the next office action.
                    </p>
                  </div>
                ) : null}
                {action === "HOLD" ? (
                  <div className="space-y-2">
                    <Label htmlFor="workflow-hold-reason">Hold reason</Label>
                    <NativeSelect
                      id="workflow-hold-reason"
                      autoFocus
                      required
                      className="w-full [&_select]:h-10"
                      value={reason}
                      onChange={(event) =>
                        setReason(
                          event.target.value as PendingJobPipelineReasonCode,
                        )
                      }
                    >
                      <option value="" disabled>
                        Choose a reason
                      </option>
                      {Object.entries(REASONS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </NativeSelect>
                    <p className="text-sm text-muted-foreground">
                      The reason stays visible on the card.
                    </p>
                  </div>
                ) : null}
                {action === "RESUME" ? (
                  <div className="space-y-2">
                    {context.holdReasonCode ? (
                      <p className="text-sm text-muted-foreground">
                        On hold: {REASONS[context.holdReasonCode]}
                      </p>
                    ) : null}
                    <p>Return this work to its previous waiting stage.</p>
                    {context.followUpAt ? (
                      <p className="text-sm text-muted-foreground">
                        The existing follow-up stays in place.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </fieldset>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 border-t p-5">
            <Button
              variant="outline"
              className="h-10"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="h-10"
              disabled={!allowed || loading || busy || stale}
            >
              {busy
                ? "Saving…"
                : action === "HOLD"
                  ? "Put on hold"
                  : action === "RESUME"
                    ? "Resume work"
                    : "Save"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
function actionError(cause: unknown): string {
  if (cause instanceof PipelineApiError) {
    if (cause.status === 409)
      return "This work changed. Reload its details before saving again.";
    if (cause.status === 404)
      return "This work is no longer waiting. Close the panel and refresh the board.";
    if (cause.status === 403)
      return "Your access changed. Ask an admin to review Pipeline permissions.";
    if (cause.code === "FOLLOW_UP_MUST_BE_FUTURE")
      return "Choose a future date and time.";
    if (cause.code === "WORKFLOW_OWNER_UNAVAILABLE")
      return "That owner is unavailable. Choose another office member.";
  }
  return "Changes could not be saved. Check your connection and try again.";
}
